/* eslint-disable no-plusplus */
import format from 'date-fns/format';

const fractionUnitOfMeasure = 'inch';
const secondsUnitOfMeasure = 'seconds';
const integerValueFormatter = (props) => {
  let initialValue = props.value;
  if (initialValue && isNaN(initialValue) && initialValue.id) {
    initialValue = props.value.id;
  }
  return initialValue;
};
const convert24To12 = ({ value }) => {
  if (!value) return null;

  const timeSplit = value.split(':');
  let hours = timeSplit[0];
  const minutes = timeSplit[1];
  let meridian;
  if (hours > 12) {
    meridian = 'PM';
    hours -= 12;
  } else if (hours < 12) {
    meridian = 'AM';
    if (+hours === 0) {
      hours = 12;
    }
  } else {
    meridian = 'PM';
  }
  const newValue = `${hours}:${minutes} ${meridian}`;
  return newValue;
};
const formatUTCtoLocalDateTime = (date) => {
  if (date) {
    const outFormat = 'MM-dd-yyyy hh:mm aaa';
    const input = date.indexOf('Z') > -1 ? new Date(date) : new Date(`${date}Z`);
    date = format(input, outFormat);
  }
  return date;
};

const convertToHoursAndMinutes = (value) => {
  const hours = Math.floor(value / 3600);
  const time = value - hours * 3600;
  const minutes = Math.floor(time / 60);
  return [hours, minutes];
};

const toNearestSixteenth = (totalInches, maxGaugeDenominator) => {
  let feet = 0;
  let inches = 0;
  feet = parseInt(totalInches / 12);
  inches = parseFloat(totalInches % 12);

  const inchFractions = (value) => {
    const denominator = maxGaugeDenominator || 4;
    const integer = parseInt(value);
    const numerator = parseInt((Math.abs(value) - Math.abs(integer)) * denominator + 0.5);
    if (denominator > 1) {
      if (integer !== 0) {
        return [integer, numerator, denominator];
      }
      if (value < 0) {
        return [0, numerator, denominator];
      }
      return [0, numerator, denominator];
    }
    return [integer, 0, 4];
  };

  const inchFractionsFinal = inchFractions(inches);
  return [feet, inchFractionsFinal[0], inchFractionsFinal[1], inchFractionsFinal[2]];
};

const convertToFraction = (val, unit, targetUnits, maxGaugeDenominator) => {
  const isFeetInchFraction = (unit && unit.toLowerCase() === 'inch') ?? 'inch';
  let results;
  if (isFeetInchFraction) {
    results = toNearestSixteenth(Number(val), maxGaugeDenominator);
  } else {
    results = '';
  }

  return `${results[0]}'${results[1]} ${results[2]}/${results[3]}"`;
};
const formatGaugeValues = (data) => {
  return convertToFraction(data.value || 0);
};

const formatTimeValues = (data) => {
  if (data.value && data.value !== 0) {
    const time = convertToHoursAndMinutes(data.value);
    const hours = time[0] === 0 ? '00' : time[0];
    const minutes = time[1] === 0 ? '00' : time[1];
    return `${hours} : ${minutes}`;
  }
  return null;
};
const formatPicklistValues = (data) => {
  // const sid = data.colDef.sid;
  // const name = data.value && dataRef && sid ?
  //   dataRef.current.picklistOptions[sid]?.find(picklistValue => picklistValue.id === data.value)?.n :
  //   null;

  return data.valueStr;
  // return name ?? data.data.valueStr?.[field];
};
const rowDataBoolFormatter = (rowData) => {
  if (!rowData || rowData.value === null || rowData.value === undefined) {
    return null;
  }
  return rowData.value === true || rowData.value === 1;
};

const formatDate = (date, timezoneRegardless, outputFormat, inputFormat) => {
  if (!date) return null;
  if (!outputFormat) return null;
  let input = new Date(date);

  // directly sending a date string to date constructor was resulting in 1 day less in PST.Below condition fixes it.
  if (inputFormat === 'yyyy-MM-dd' && !timezoneRegardless) {
    const dateArray = date.split('-');
    const year = dateArray[0];
    const month = parseInt(dateArray[1], 10) - 1;
    const day = dateArray[2];
    input = new Date(year, month, day);
  }

  if (timezoneRegardless === true) {
    input = new Date(input.valueOf() + input.getTimezoneOffset() * 60 * 1000);
  }

  const formattedDate = format(input, outputFormat);

  return formattedDate;
};

const formatDateVariant6 = (date, timezoneRegardless = false) => {
  const outputFormat = 'MM-dd-yyyy hh:mm aaa';

  return formatDate(`${date}Z`, timezoneRegardless, outputFormat);
};
const rowDataDateFormatter = (rowData, timezoneRegardless = false) => {
  if (!rowData || !rowData.value) {
    return null;
  }
  return formatDateVariant6(rowData.value, timezoneRegardless);
};

const DATA_TYPES = Object.freeze({
  PICKLIST: 'picklist',
  LOOKUP: 'lookup',
  // Numeric
  DECIMAL: 'decimal',
  INTEGER: 'integer',
  NUMBER: 'number',
  NUMERIC: 'numeric',
  INT: 'int',
  BIGINT: 'bigint',
  // Strings
  STRING: 'string',
  VARCHAR: 'varchar',
  NVARCHAR: 'nvarchar',
  NTEXT: 'ntext',
  TEXT: 'text',
  // Date
  DATE: 'date',
  DATETIME: 'datetime',
  TIME: 'time',
  // Boolean
  BOOLEAN: 'boolean',
  BIT: 'bit',

  UUID: 'uniqueidentifier',
});

const hideBits = {
  None: 0x0,
  RemoveFromGrid: 0x1,
  HideOnForm: 0x2,
  Disable: 0x4,
  HideOnGrid: 0x8,
};

const isAttributesToBeShown = (pivotColData, outliers) => {
  const bitValue = pivotColData.HideBits;
  const sequence = pivotColData.Sequence;
  let found = false;

  found =
    !!pivotColData.AttrName &&
    outliers.toString().toLowerCase().split(',').indexOf(pivotColData.AttrName.toLowerCase()) > -1;

  if (found) {
    return true;
  }

  // eslint-disable-next-line no-bitwise
  return pivotColData.IsSelected && !!sequence && (hideBits.HideOnForm & bitValue) === 0;
};

const addToGrid = (pivotColData) => {
  const outliers = [
    'TicketDate',
    'ReadingDate',
    'ReadingTime',
    'TicketTime',
    'CreatedBy',
    'CreatedOn',
    'ModifiedOn',
    'ModifiedTimestamp',
    'ModifiedBy',
    'IsDeleted',
    'CalculationTypeID',
    'DataInputMethodID',
    'AuthorityLevelID',
    'IsFinalized',
    'ReadingNumber',
  ];
  return isAttributesToBeShown(pivotColData, outliers);
};

const PAYLOAD_KEYS = Object.freeze({
  // General Keys
  TOTAL_COUNT: 'tc',
  TIME_ZONE_OFFSET: 'TzOffset',
  TIME_ZONE: 'TimeZone',
  ERRORS: 'Errors',
  ASYNC_STATUS: 'AsyncStatus',
  LAST_EVALUATED_KEY: 'LastEvaluatedKey',
  DATA: 'Data',
  LIMIT: 'Limit',
  PAGE: 'page',
  RESULT: 'Result',
  PREFIX: 'Prefix',
  OPERATIONS: 'Operations',
  ALLOCATION: 'Allocation',
  ROW_ID: 'RowId',
  STATUS: 'Status',
  NAME_2: 'name',

  // Components - Assets
  WELL_TYPE_ID: 'wtid',
  PRIMARY_TRANSPORTER_ID: 'primtranid',
  PRIMARY_TRANSPORTER_NAME: 'primtrann',
  SECONDARY_TRANSPORTER_ID: 'sectranid',
  SECONDARY_TRANSPORTER_NAME: 'sectrann',
  PRIMARY_PURCHASER_ID: 'primpurid',
  PRIMARY_PURCHASER_NAME: 'primpurn',
  SECONDARY_PURCHASER_ID: 'secpurid',
  SECONDARY_PURCHASER_NAME: 'secpurn',
  PRODUCING_METHOD_ID: 'pmtid',
  WELL_TEST_RECURRENCE: 'wtr',
  DAILY_RESET: 'isreset',
  STOP_NAME: 'stpn',
  WELL_TYPE_NAME: 'wtn',
  DELIVERY_CONFIGURATION_ID: 'cfgid',
  DELIVERY_CONFIGURATION_NAME: 'cfgn',
  PRODUCT_TYPE_NAME: 'prtn',
  PRODUCING_METHOD_NAME: 'pmtn',
  CONTRACT_HOUR_ENABLED: 'che',
  IS_ASSET_BULK_EDIT: 'isBulkEdit',

  // TODO: multiple different property names for one value
  TANK_TYPE_ID: 'tid',
  METER_TYPE_ID: 'tid',
  EQUIPMENT_TYPE_ID: 'tid',

  PRODUCING_STATUS_ID: 'pstid',
  PRODUCT_SUB_TYPE_ID: 'pstid',

  PRODUCING_TYPE_ID: 'prtid',
  PRODUCT_TYPE_ID: 'prtid',

  PRODUCING_STATUS_NAME: 'pstn',
  PRODUCT_SUB_TYPE_NAME: 'pstn',

  STOP_LONGITUDE: 'lng',
  LONGITUDE: 'lng',

  STOP_LATITUDE: 'lat',
  LATITUDE: 'lat',

  TANK_STRAPPING_DESCRIPTION: 'tsiddesc',
  ALLOCATION_DISPOSITION_ID: 'aldpid',
  ALLOCATION_DISPOSITION_NAME: 'aldpn',
  TANK_TYPE_NAME: 'ttn',
  USER_FIELD_VALUES: 'UserFieldValues',
  SYSTEM_FIELD_VALUES: 'SystemFieldValues',
  METER_TYPE_NAME: 'mtn',
  EQUIPMENT_TYPE_NAME: 'eqtn',
  IDS: 'ids',
  // Components - Picklists
  ID: 'id',
  NAME: 'n',
  CREATED_BY: 'crby',
  CREATED_ON: 'cron',
  CREATED_AT: 'CreatedAt',
  MODIFIED_BY: 'mdby',
  MODIFIED_ON: 'mdon',
  MODIFIED_AT: 'ModifiedAt',
  CREATED_BY_NAME: 'crbyn',
  MODIFIED_BY_NAME: 'mdbyn',
  UUID: 'UUID',
  DISPLAY_NAME: 'dn',
  CUSTOMIZABLE: 'icble',
  PARENT_PICKLIST_OPTIONS_SET_ID: 'pplistsid',
  PARENT_PICKLIST_OPTIONS_SET_NAME: 'pplistsidname',
  PICKLIST_OPTIONS_SET_ID: 'sid',
  PARENT_PICKLIST_OPTION_ID: 'pplistid',
  PARENT_PICKLIST_ID_NAME: 'pplistidname',
  VALUE: 'v',
  CODE: 'code',
  ADDRESS: 'ad',
  CITY: 'ct',
  STATE: 'st',
  ZIP_CODE: 'zc',
  PHONE_NUMBER: 'pn',
  DESCRIPTION: 'd',
  IS_ACTIVE: 'IsActive',
  IS_ACT: 'act',
  PURCHASER_ID: 'purid',
  PURCHASER_NAME: 'purn',
  PURCHASER_SOURCE_ID: 'srcid',
  XID: 'xid',
  IS_FOR_WATER: 'ifw',
  IS_FOR_OIL: 'ifo',
  IS_FOR_NGL: 'ifngl',
  SORT_ORDER: 'so',
  TANK_STRAPPING_ID: 'tsid',
  TANK_STRAPPING_NAME: 'tsidn',
  FROM_HEIGHT: 'frmh',
  TO_HEIGHT: 'toh',
  VOLUME_PER_INCH: 'vpi',

  // Sorting
  ASCENDING: 'ASC',
  DESCENDING: 'DESC',
  SORT_GRID_ASCENDING: 'asc',
  SORT_GRID_DESCENDING: 'desc',

  // My own
  UIDS: 'uids',
  // Manage-Networks
  ALLOCATION_UOM: 'AllocationUOM',
  NETWORK_NAME: 'NetworkName',
  NETWORK_GROUP_NAME: 'NetworkGroupName',
  NETWORK_GROUP_ID: 'NetworkGroupId',
  READING_CONFIG_ID: 'ReadingConfigId',
  NETWORK_VERSION_ID: 'NetworkVersionId',
  NETWORK_GROUP_PATH: 'NetworkGroupPath',
  NETWORK_GROUP: 'NetworkGroup',
  HIERARCHY_ID: 'HierarchyId',
  OBJECT_NAME: 'ObjectName',
  OBJECT_TYPE: 'ObjectType',
  READING_CONFIG_NAME: 'ReadingConfigName',
  VOLUME_SOURCES: 'VolumeSources',
  MONTHLY_VOLUME_SOURCES: 'MonthlyVolumeSources',
  PATH: 'Path',
  LOCKED_ALLOCATION: 'LockedAllocation',
  DAILY_RESULT_ID: 'DailyResultId',
  DAILY_RESULT: 'DailyResult',
  IS_VIRTUAL: 'IsVirtual',
  CUSTOM_MONTHLY: 'CustomMonthly',
  EXCEPTION_TYPE: 'excptn_type',
  EXCEPTION_DATA: 'excptn_data',
  ENERGY_UOM: 'EnergyUOM',
  IS_ENERGY: 'IsEnergy',

  // Operations - Automated pump by exception
  READING_VIEW: 'ReadingView',
  START_SCORE: 'StartScore',
  STOP: 'Stop',
  ALERT_NAME: 'AlertName',
  LABEL: 'Label',
  ALERT_ID: 'AlertId',
  SCHEDULE_NAME: 'ScheduleName',
  OBJECT_TYPE_KEY: 'ObjectType',
  OBJECT_SUB_TYPES_KEY: 'ObjectSubtypes',
  TEAMS_KEY: 'Teams',
  LABEL_KEY: 'Label',
  DESCRIPTION_KEY: 'Description',
  IMPACT_PARAMS: 'ImpactParams',
  INCREMENT_PERCENTAGE: 'IncrPerc',
  INCREMENT: 'Increment',
  ALERT_DESCRIPTION: 'ad',
  ALERT_LABEL: 'al',
  EXPRESSION: 'Expression',
  MAPPING: 'Mapping',
  VARIABLES: 'Variables',
  TRIGGER_CONFIG: 'TriggerConfig',
  TRIGGER_ENABLED: 'TriggerEnabled',
  FSM_FIELD: 'FSMField',
  IS_FSM: 'IsFSM',
  FSM_TASK_TYPE: 'FSMTaskType',
  RUN_CONFIG: 'RunConfig',
  CREATE: 'Create',
  UPDATE: 'Update',
  RESOLVE: 'Resolve',
  RULE: 'Rule',
  RULE_ID: 'RuleId',
  RULE_NAME: 'RuleName',
  OBJECT_SCOPE: 'Scope',
  TEAMS: 'Teams',
  FREQUENCY: 'Frequency',
  DAYS: 'Days',
  DISPLAY_DAYS: 'DisplayDays',
  RULES: 'Rules',
  TEAM_NAMES: 'TeamNames',

  // Operations Network
  EFFECTIVE_FROM: 'efrm',
  EFFECTIVE_TO: 'efto',
  USER_STRING1: 'usrstr1',
  USER_STRING2: 'usrstr2',
  USER_STRING3: 'usrstr3',
  USER_STRING4: 'usrstr4',
  USER_STRING5: 'usrstr5',
  USER_INT1: 'usrint1',
  USER_INT2: 'usrint2',
  USER_INT3: 'usrint3',
  USER_INT4: 'usrint4',
  USER_INT5: 'usrint5',
  USER_DECIMAL1: 'usrdec1',
  USER_DECIMAL2: 'usrdec2',
  USER_DECIMAL3: 'usrdec3',
  USER_DECIMAL4: 'usrdec4',
  USER_DECIMAL5: 'usrdec5',
  USER_DATE_TIME1: 'usrdate1',
  USER_DATE_TIME2: 'usrdate2',
  USER_DATE_TIME3: 'usrdate3',
  USER_DATE_TIME4: 'usrdate4',
  USER_DATE_TIME5: 'usrdate5',
  ROUTE_TYPE_ID: 'rtid',
  ROUTE_TYPE_NAME: 'rtn',
  FIELD: 'field',
  FIELD_NAME: 'fldn',
  FIELD_ID: 'fldid',
  AREA_ID: 'aid',
  ROUTE_FREQUENCY: 'rf',
  NUMBER_OF_STOPS: 'ns',
  FIRST_READING_DATE: 'frd',
  LAST_READING_DATE: 'lrd',
  LAST_READER_ID: 'lrid',
  CONTRACT_HOUR_TIME: 'cht',
  START_LOCATION_ID: 'StartLocationID',
  END_LOCATION_ID: 'EndLocationID',
  START_GEOLOCATION_ID: 'StartGeolocationID',
  START_LOCATION_LATITUDE: 'StartLocationLatitude',
  START_LOCATION_LONGITUDE: 'StartLocationLongitude',
  END_GEOLOCATION_ID: 'EndGeolocationID',
  END_LOCATION_LATITUDE: 'EndLocationLatitude',
  END_LOCATION_LONGITUDE: 'EndLocationLongitude',
  ROUTE_ID: 'rutid',
  ROUTE_NAME: 'rutn',
  STOP_TYPE_ID: 'stptid',
  STOP_TYPE_NAME: 'stptn',
  NUMBER_OF_OBJECTS: 'nobj',
  STOP_PERIMETER_TYPE: 'spt',
  POLYGON_CORDINATES: 'pc',
  // TODO: three different property names for one sequence value
  ROUTE_SEQUENCE: 'seqn',
  SEQUENCE_NUMBER: 'seqn',
  SEQ_NUMBER: 'seq',

  GEO_LOCATION_ID: 'GeoLocationID',
  STOP_OBJECT: 'stpobj',
  GEO_LOCATION: 'Geolocation',
  TOTAL_SCORE: 'TotalScore',
  TOTAL_DURATION: 'TotalDuration',
  PRIORITY: 'Priority',
  SCORE: 'Score',
  DYNAMIC_ROUTE_INDEX: 'DynamicRouteIndex',
  STOP_DISTANCE: 'StopDistance',
  CUSTOM_ROUTE_SEQUENCE: 'cseqn',
  WORK_ESTIMATE: 'WorkEstimate',
  IMPACT_SCORE: 'ImpactScore',
  VISIT_FREQUENCY: 'VisitFrequency',

  // TODO: two different property names for one route IDs value
  ROUTE_IDS: 'RouteIDs',
  ROUTE_IDS_2: 'RouteIds',
  ROUTES: 'ruts',

  ROUTE_NAMES: 'RouteNames',
  RADIUS: 'rad',
  STOP_COORDINATES: 'scds',
  PAD_NAME: 'PadName',
  USERNAME: 'un',
  FIRST_NAME: 'fn',
  LAST_NAME: 'ln',
  EMAIL_ADDRESS: 'em',
  PUSH_NOTIFICATION_TOKEN_ID: 'tknid',
  AUTHORITY_LEVEL_ID: 'alid',
  ROLE_IDS: 'rids',
  ROLE_NAMES: 'rns',
  TEAM_ID: 'tmid',
  TEAM_NAME: 'tmn',
  FIELD_IDS: 'fldids',
  FIELD_NAMES: 'fldns',
  SCADA_READING_TIME: 'ScadaReadingTime',
  PRIMARY_PUMPER_ID: 'puid',
  PRIMARY_PUMPER_NAME: 'pper',
  SECONDARY_PUMPER_ID: 'suid',
  SECONDARY_PUMPER_NAME: 'sper',
  ALTERNATE_PUMPER_IDS: 'acids',
  ALTERNATE_PUMPER_NAMES: 'apers',
  STOP_IDS: 'stps',
  IS_ALL_SELECTED: 'isAllSelected',
  SEARCH_PHRASE: 'searchPhrase',
  FILTER: 'filters',
  SEQUENCE_IDS: 'seqids',
  STOP_OBJECT_TYPE_ID: 'obtid',
  STOP_OBJECT_TYPE_NAME: 'obtn',
  STOP_OBJECT_ID: 'stpobjid',
  STOP_ID: 'stpid',
  STOP_OBJECTS: 'stpobjs',
  IS_CUSTOM: 'ic',
  ALLOW_MULTIPLE_READING_PER_DAY: 'amrd',
  READING_RECURRENCE_NAME: 'rrn',
  READING_RECURRENCE_ID: 'rrid',
  LAST_KNOWN_READINGS: 'lkr',
  READINGS_TO_RESTORE: 'rtr',
  RECURRENCE_LIMIT_VALUE: 'rrv',
  RECURRENCE_LIMIT_TYPE_ID: 'rrt',
  OBJECT_SUB_TYPE_IDS: 'ostids',
  OBJECT_SUB_TYPE_NAMES: 'ostns',
  ENTITY_ID: 'eid',
  ATTRIBUTE_TYPE_NAME: 'atttn',
  ATTRIBUTE_TYPE_ID: 'tid',
  UNIT_OF_MEASURE: 'uom',
  PARENT_ATTRIBUTE_ID: 'prnid',
  PICKLIST_OPTION_SET_NAME: 'setn',
  IS_SEARCHABLE: 'iss',
  IS_ADJUSTABLE: 'iadj',
  IS_DISABLED: 'dbed',
  IS_REQUIRED: 'reqd',
  IS_ALLOCATION_VOLUME: 'iav',
  IS_INTERPOLABLE: 'iip',
  IS_AVERAGE_BACK: 'iab',
  IS_SPARKLINE: 'isl',
  ALLOW_PICTURES: 'ap',
  PICTURES_REQUIRED: 'pr',
  ALLOW_BULK_EDIT: 'abe',
  IS_HIDDEN: 'ihdn',
  IS_ONE_TIME_SYNC: 'iotc',
  IS_CALCULATED: 'ica',
  IS_RESET_ALLOWED: 'ira',
  IS_FORCE_CALCULATED: 'forceccal',
  IS_FORCE_CARRY_FORWARD: 'forceccf',
  ENTITY_NAME: 'en',
  OBJECT_SUB_TYPE_NAME: 'obstn',
  OBJECT_SUB_TYPE: 'obst',
  OBJECT_SUBTYPE: 'ObjectSubtype',
  OBJECT_SUBTYPES: 'ObjectSubtypes',
  OBJECT_SUB_TYPE_ID: 'obstid',
  IS_MASS_CARRY_FORWARD_ENABLED: 'IsMassCarryForwardEnabled',
  ENTITY_VIEW_ID: 'envid',
  CALCULATION_FORMULA: 'cf',
  VALIDATION_FORMULA: 'cvf',
  CARRY_FORWARD_TYPE_NAME: 'cftn',
  CARRY_FORWARD_TYPE_ID: 'cft',
  CARRY_FORWARD_FORMULA: 'cff',
  FORMATTING_FORMULA: 'ff',
  MIN_LENGTH: 'minl',
  MAX_LENGTH: 'maxl',
  MIN_VALUE: 'minv',
  MAX_VALUE: 'maxv',
  PRECISION: 'prec',
  STRUCK_DATA_OCCURRENCE: 'sdo',
  IS_SELECTED: 'issel',
  IS_EXCEPTION_ENABLED: 'iee',
  IS_FOR_GRID: 'ifg',
  IS_FOR_CHART: 'ifc',
  LINE_COLOR: 'lc',
  CHART_AGGREGATOR_NAME: 'can',
  CHART_AGGREGATOR_ID: 'caid',
  ATTRIBUTE_ID: 'attid',
  ATTRIBUTE_NAME: 'attn',
  RESET_VALUE: 'rv',
  DEFAULT_VALUE: 'DefaultValue',
  SCHEMA_NAME: 'schn',
  MIN_READING_RECURRENCE: 'mrr',
  RECURRENCE_LIMIT_TYPE_NAME: 'rrtn',
  LIMIT_DATE_RANGE: 'ldr',
  COLOR_CODE: 'clr',
  SHOW_IN_EOR: 'ShowInEOR',
  TEMPLATE_IMAGE_ID: 'TemplateImageID',
  TRANSPORTER_ID: 'TransporterID',
  TRANSPORTER_NAME: 'TransporterName',
  PARSING_RULE: 'ParsingRule',
  IMAGE_PATH: 'ImagePath',
  // TODO: <
  // Components - Assets - Configure Asset Attributes
  AUGMENTED_OBJECT_TYPE_NAME: 'AugmentedObjectType.n',
  AUGMENTED_OBJECT_TYPE: 'AugmentedObjectType',
  AUGMENTED_OBJECT_TYPE_ID: 'AugmentedObjectTypeID',
  GROUP_ID_ASSET: 'gid',
  GROUP_NAME_ASSET: 'gn',
  USER_FIELD: 'UserField',
  USER_FIELD_ATTRIBUTES: 'ufas',
  USER_FIELD_ID: 'ufid',
  USER_FIELD_TYPE_NAME: 'uftn',
  USER_FIELD_SET_NAME: 'setn',
  USER_FIELD_TYPE: 'UserFieldType',
  USER_FIELD_TYPE_ID: 'uftid',
  USER_FIELD_DISPLAY_NAME: 'UserFieldDisplayName',
  PICKLIST_OPTIONS_SET: 'PicklistOptionsSet',
  IS_BULK_EDIT: 'be',
  NODE_ID: 'nid',
  // Manage Access - Users
  USER_ID: 'UserId',
  USER_XID: 'UserXID',
  EMAIL: 'Email',
  GIVEN_NAME: 'GivenName',
  FAMILY_NAME: 'FamilyName',
  SUPER_ADMIN: 'Super Admin',
  IS_PRIMARY: 'IsPrimary',
  IT_ADMIN: 'IT Admin',
  PENDING_INVITE: 'PendingInvite',
  DISPLAY_ROLES: 'DisplayRoles',
  ALLOCATION_ROLES: 'AllocationRoles',
  ANALYTICS_ROLES: 'AnalyticsRoles',
  ANALYTICS: 'Analytics',
  FDG_ROLES: 'FDGRoles',
  MODULES: 'Modules',
  USERS: 'Users',
  VALID: 'Valid',
  INVALID: 'Invalid',
  VALID_ROWS: 'ValidRows',
  VALID_COUNT: 'ValidCount',
  INVALID_COUNT: 'InvalidCount',
  ALLOCATION_ADMIN: 'AllocationAdmin',
  ALLOCATION_VIEW: 'AllocationView',
  SECURITY_ADMIN: 'SecurityAdmin',
  OPERATIONS_ADMIN: 'OperationsAdmin',
  FOREMAN: 'Foreman',
  PUMPER: 'Pumper',
  ANALYTICS_ADMIN: 'AnalyticsAdmin',
  ANALYTICS_USER: 'AnalyticsUser',
  ALLOCATION_GROUPS: 'AllocationGroups',
  ANALYTICS_GROUPS: 'AnalyticsGroups',
  OPERATION_TEAMS: 'OperationTeams',
  // Manage Access - Teams-and-groups
  GROUP_ID: 'group_id',
  GROUP_DISPLAY_NAME: 'disply_name',
  GROUP_DISPLAY_NAME_ADD: 'group_display_name',
  GROUP_DESCRIPTION: 'description',
  GROUP_DESCRIPTION_ADD: 'group_description',
  USER_FIRST_NAME: 'first_name',
  USER_LAST_NAME: 'last_name',
  USER_EMAIL: 'email_address',
  GROUP_USER_ID: 'user_id',
  GROUP_LIST: 'group_list',
  USER_KEY: 'user_key',
  USER_LIST: 'user_list',
  // readings management
  READINGS_MANAGEMENT_ATTRIBUTE_NAME: 'field',
  READINGS_MANAGEMENT_ATTRIBUTE_DISPLAY_NAME: 'headerName',
  READINGS_MANAGEMENT_ATTRIBUTE_TYPE_NAME: 'type',
  READINGS_MANAGEMENT_UNIT_OF_MEASURE: 'Unit',
  READINGS_MANAGEMENT_IS_REQUIRED: 'required',
  READINGS_MANAGEMENT_IS_DISABLED: 'disabled',
  READINGS_MANAGEMENT_READING_DATE: 'ReadingDate',
  READINGS_MANAGEMENT_TICKET_DATE: 'TicketDate',
  READINGS_MANAGEMENT_READING_TIME: 'ReadingTime',
  READINGS_MANAGEMENT_TICKET_TIME: 'TicketTime',
  READINGS_MANAGEMENT_READING_ID: 'ReadingID',
  READINGS_MANAGEMENT_READING_NUMBER: 'ReadingNumber',
  READINGS_MANAGEMENT_TICKET_NUMBER: 'TicketNumber',
  READINGS_MANAGEMENT_ID: 'ID',
  READINGS_MANAGEMENT_COMMENTS: 'Comments',
  READINGS_MANAGEMENT_OBJECT_ID: 'ObjectID',
  READINGS_MANAGEMENT_OBJECT_TYPE_ID: 'ObjectTypeID',
  READINGS_MANAGEMENT_AUTHORITY_LEVEL_ID: 'AuthorityLevelID',
  READINGS_MANAGEMENT_READING_ENTITY_ID: 'ReadingEntityID',
  READINGS_MANAGEMENT_DATA_INPUT_METHOD_ID: 'DataInputMethodID',
  READINGS_MANAGEMENT_CALCULATION_TYPE_ID: 'CalculationTypeID',
  READINGS_MANAGEMENT_IS_DELETED: 'IsDeleted',
  READINGS_MANAGEMENT_UUID: 'UUID',
  READINGS_MANAGEMENT_MODIFIED_BY: 'ModifiedBy',
  READINGS_MANAGEMENT_MODIFIED_ON: 'ModifiedOn',
  READINGS_MANAGEMENT_CREATED_BY: 'CreatedBy',
  READINGS_MANAGEMENT_CREATED_ON: 'CreatedOn',
  READINGS_MANAGEMENT_CREATED_TIME_STAMP: 'CreatedTimestamp',
  READINGS_MANAGEMENT_MODIFIED_TIME_STAMP: 'ModifiedTimestamp',
  READINGS_MANAGEMENT_IS_FINALIZED: 'IsFinalized',
  READINGS_MANAGEMENT_PURCHASE_ID: 'PurchaserID',
  READINGS_MANAGEMENT_TRANSPORTER_ID: 'TransporterID',
  READINGS_MANAGEMENT_SYSTEM_CALCULATED: 'systemCalculated',
  PIVOT_COL_DATA: 'PivotColData',
  PIVOT_RECORDS: 'PivotRecords',
  // Allocations-Manage Networks - Inventory
  PRODUCT_ID: 'ProductId',
  PRODUCT_NAME: 'ProductName',
  DISPOSITION_ID: 'DispositionId',
  DISPOSITION_NAME: 'DispositionName',
  NETWORK_ID: 'NetworkId',
  TANK_ID: 'TankId',
  TANK_IDS: 'TankIds',
  INVENTORY: 'Inventory',
  CLOSE_DATE: 'CloseDate',
  CLOSE_ON: 'CloseOn',
  OBJECT_ID: 'ObjectId',
  END_DATE: 'EndDate',
  START_DATE: 'StartDate',
  START_MONTH: 'StartMonth',
  END_MONTH: 'EndMonth',
  PRODUCT: 'Product',
  BEGIN_INVENTORY: 'BeginInventory',
  END_INVENTORY: 'EndInventory',
  DATE: 'Date',
  LOCKED_DATE: 'LockedDate',
  Locked_STATUS: 'LockedStatus',
  GROSS_SALES: 'GrossSales',
  OBJECT: 'Object',
  PRODUCTION: 'Production',
  SALES: 'Sales',
  TRANSACTIONS: 'Transactions',
  ALLOCATION_BASIS: 'AllocationBasis',
  INVENTORY_DATE: 'InventoryDate',
  TRANSACTION_DATE: 'TransactionDate',
  UPDATE_DATE: 'UpdateDate',
  UPDATED_DATE: 'UpdatedDate',
  UPDATE_TYPE: 'UpdateType',
  VOLUME_IN: 'VolumeIn',
  VOLUME_OUT: 'VolumeOut',
  VOLUME_REMAIN: 'VolumeRemain',
  WELL_COMPLETION: 'WellCompletion',
  STOP_NAME_INVENTORY: 'StopName',
  READING_DATE: 'ReadingDate',
  TICKET_DATE: 'TicketDate',
  READING_TYPE: 'ReadingType',
  GROSS_VOLUME: 'GrossOilVol',
  NET_METER_VOLUME: 'NetVolume',
  NET_VOLUME: 'NetOilVol',
  LACT_METER_ID: 'LACTMeterID',
  TYPE: 'type',
  TYPE_2: 'Type',
  OBJECT_ID_METER: 'meterId',
  OBJECT_ID_TANK: 'tankId',
  FIFO_PRIORITY: 'FIFOPriority',
  WELL_PRIORITY: 'WellPriority',
  LAST_UPDATE_DATE: 'LastUpdateDate',

  // Allocations - Reconciliation
  STANDARD_PRESSURE: 'StdPressure',
  STANDARD_TEMPERATURE: 'StdTemperature',

  // Allocations - Scheduler
  SCHEDULER_SCHEDULE_NAME: 'ScheduleName',
  SCHEDULER_TIME_ZONE: 'TimeZone',
  SCHEDULER_RUN_TIME: 'RunTime',
  SCHEDULER_ALLOCATION_DATE: 'AllocDay',
  SCHEDULER_PRODUCTS: 'Products',
  SCHEDULER_RESOURCES: 'Resources',
  SCHEDULER_SCHEDULE: 'Description',
  SCHEDULER_MODIFIED_ON: 'ModifiedAt',
  SCHEDULER_MODIFIED_BY: 'ModifiedBy',
  SCHEDULER_ID: 'ScheduleId',
  SCHEDULER_CRON_DETAILS: 'CronDetails',
  SCHEDULER_DUE_DATE: 'DueDate',
  SCHEDULER_DUE_DATE_DESCRIPTION: 'DueDateDescription',
  SCHEDULER_RUN_DATE_DESCRIPTION: 'Description',
  SCHEDULER_RUN_DATE: 'RunDate',
  SCHEDULER_PA_RESPONSIBLE: 'pa',
  SCHEDULER_ALLOCATION_ROLES: 'sar',
  SCHEDULER_SCHEDULED_TIME: 'st',
  // Manage Access - Teams & Groups
  TEAM_FDG_NAME: 'n',
  TEAM_FDG_DESCRIPTION: 'd',
  TEAM_FDG_ID: 'id',
  TEAM_MEMBER_FDG_EMAIL: 'em',
  TEAM_MEMBER_FDG_FIRST_NAME: 'fn',
  TEAM_MEMBER_FDG_LAST_NAME: 'ln',
  TEAM_MEMBER_FDG_USER_NAME: 'un',
  TEAM_FDG_VIEW_USER_NAME: 'tmn',
  TEAM_MEMBERS_FDG_ADD_ID: 'tmid',
  TEAM_MEMBERS_FDG_ADD_EMAIL_ID: 'ems',
  // Account Settings - Account Info
  ACCOUNT_INFO_TENANT_NAME: 'TenantName',
  ACCOUNT_INFO_TENANT_TITLE: 'TenantTitle',
  ACCOUNT_INFO_TENANT_WEBSITE: 'TenantWebsite',
  ACCOUNT_INFO_TENANT_PRIMARY_CONTACT: 'PrimaryContact',
  ACCOUNT_INFO_TENANT_GIVEN_NAME: 'GivenName',
  ACCOUNT_INFO_TENANT_FAMILY_NAME: 'FamilyName',
  ACCOUNT_INFO_TENANT_EMAIL: 'Email',
  ACCOUNT_INFO_TENANT_PHONE_NUMBER: 'PhoneNumber',
  ACCOUNT_INFO_TENANT_PLAN_TYPE: 'PlanType',
  ACCOUNT_INFO_TENANT_SUBSCRIPTION_TYPE: 'SubscriptionType',
  ACCOUNT_INFO_TENANT_PLAN_START: 'PlanStart',
  ACCOUNT_INFO_TENANT_PLAN_END: 'PlanEnd',
  ACCOUNT_INFO_TENANT_ADDRESS: 'Address',
  ACCOUNT_INFO_TENANT_CITY: 'City',
  ACCOUNT_INFO_TENANT_STATE: 'State',
  ACCOUNT_INFO_TENANT_ZIP_CODE: 'ZIP',
  // Account Settings - Invoices
  ACCOUNT_INFO_TENANT_INVOICE_NUMBER: 'InvoiceId',
  ACCOUNT_INFO_TENANT_INVOICE_DATE: 'InvoiceDate',
  ACCOUNT_INFO_TENANT_INVOICE_PERIOD: 'InvoicePeriod',
  ACCOUNT_INFO_TENANT_INVOICE_AMOUNT: 'AmountDue',
  ACCOUNT_INFO_TENANT_INVOICE_TAX: 'Tax',
  ACCOUNT_INFO_TENANT_INVOICE_DUE_DATE: 'DueDate',
  ACCOUNT_INFO_TENANT_INVOICE_STATUS: 'Status',
  ACCOUNT_INFO_TENANT_INVOICE_PDF: 'InvoicePDF',
  CURRENT_MONTH: 'CurrentMonth',
  PREVIOUS_MONTH: 'PreviousMonth',
  LAST_THREE_MONTH: 'Last3Month',
  // Account Settings-Billing Details
  TENANT_BILLING_DETAILS: 'BillingDetails',
  TENANT_PAYMENT_METHOD: 'payment_method',
  TENANT_CREDIT_CARD: 'CreditCard',
  TENANT_CREDIT_CARD_NAME: 'Name',
  TENANT_CREDIT_CARD_EXPIRY: 'Expiry',
  // ENTITY_VIEW_NAME: 'envdn',
  // Team Views
  // Reading Formats - Stop Totals
  EMAILS: 'ems',
  ALLOCATION_GROUP_NAME: 'UserGroupName',
  ALLOCATION_GROUP_DESCRIPTION: 'Description',
  ALLOCATION_GROUP_MODIFIED_BY: 'ModifiedBy',
  ALLOCATION_MODIFIED_ON: 'ModifiedOn',
  ALLOCATION_GROUP_EDIT: 'Edit',
  ALLOCATION_GROUP_DELETE: 'Delete',
  ALLOCATION_GROUP_USER_IDS: 'UserIds',
  ALLOCATION_GROUP_USERS: 'Users',

  // Components - Reading Formats
  NAME_KEY: 'Name',
  DISPLAY_NAME_KEY: 'DisplayName',
  STOP_TOTAL_FIELD: 'StopTotalField',
  STOP_TOTAL_ID: 'ID',
  STOP_TOTAL_SIGN: 'Sign',
  FILTER_EXPRESSION_DISPLAY: 'FilterExpressionDisplay',
  STOP_TOTAL_ATTRIBUTE: 'StopTotalAttribute',
  ENTITY_VIEW_NAME: 'envdn',
  EDITABLE_DAYS: 'ed',
  EDITABLE_DAYS_ATTRIBUTE_ID: 'efdaid',
  EDITABLE_DAYS_ATTRIBUTE_NAME: 'efdan',

  // Components - Picklists - Dynamic Lookup
  VALUE_KEY: 'Value',
  ASSET_ID: 'AssetID',
  FILTERS: 'Filters',
  FILTER_ID: 'ID',
  LOOKUP_FILTER_ID: 'LookUpFilterID',
  IS_CUSTOMIZABLE: 'IsCustomizable',
  MODIFIED_ON_KEY: 'ModifiedOn',
  MODIFIED_BY_KEY: 'ModifiedBy',
  COLUMNS_DESCRIPTION: 'ColumnsDescription',
  // Account Settings - Integrations
  CATEGORY: 'c',
  MAPPING_NAME: 'mn',
  MAPPING_COUNT: 'mc',
  SOURCE_QUERY: 'sq',
  UPLOAD_STATUS: 'us',
  DATA_TYPE: 'dt',
  ALLOWS_NULL: 'an',
  IS_SYSTEM_ATTRIBUTE: 'isa',
  LOOKUP_ATTRIBUTE_NAME: 'lan',
  SOURCE_ATTRIBUTE: 'sa',
  INTEGRATIONS_DEFAULT_VALUE: 'dv',
  ATTRIBUTE_FORMULA: 'af',
  FILTER_FORMULA: 'ff',
  JOIN_ATTRIBUTES: 'ja',
  LOAD_FROM_DATE: 'lut',
  INTEGRATIONS_IS_ACTIVE: 'ia',
  LOOKUP_ENTITY_TYPE: 'let',
  LOOKUP_SUB_ENTITY: 'lse',
  JOYN_ATTRIBUTE_NAME: 'jan',
  LOOKUP_SET_ID: 'lsid',
  DYNAMIC_LOOKUP_SET_ID: 'LookupSetID',
  LOOKUP_SET_NAME: 'lsn',
  READING_TYPE_GROUP: 'rtg',
  READING_TYPE_GROUP_NAME: 'rtgn',
});

export default function readingsParser(response) {
  if (response?.success && response?.tc === 1) {
    const result = response.Result;
    const records = [];
    const pivotColumns = result[PAYLOAD_KEYS.PIVOT_COL_DATA];
    let pivotRecords = result[PAYLOAD_KEYS.PIVOT_RECORDS];
    pivotRecords.sort((b, a) => {
      // eslint-disable-next-line no-nested-ternary
      return new Date(a.ReadingDate) > new Date(b.ReadingDate)
        ? 1
        : new Date(b.ReadingDate) > new Date(a.ReadingDate)
        ? -1
        : 0;
    });
    if (!!pivotColumns && pivotColumns.length > 0) {
      if (pivotRecords.length === 0) {
        pivotRecords = [
          {
            LineItemColumns: [],
          },
        ];
      }
      // if (!dataRef.current.readingTypes || !dataRef.current.readingTypes.length) {
      //   await fetchAndSetReadingTypes();
      // }
      // TODO: needed for lookups??
      // fetchPickListOptionsAndSetCache(pivotColumns, entityViewId).catch(err => {
      //   // do something
      // });
      // TODO: needed for Modified by/Created by
      // if (!dataRef.current.users) {
      //   setUsersRef();
      // }
      for (let x = 0; x < pivotRecords.length; x++) {
        const row = { valueStr: {} };

        for (let col = 0; col < pivotColumns.length; col++) {
          const colObj = pivotRecords[x].LineItemColumns[col];
          // TODO: add sequence to the record object and sort on that

          let valueFormatter;
          const column = pivotColumns[col];
          if (x === 0) {
            if (addToGrid(column)) {
              // lookup names ref
              // if (!!column.AttrTypeName && column.AttrTypeName.toLowerCase() === DATA_TYPES.LOOKUP) {
              //   lookupNamesRef.current = [...new Set([...lookupNamesRef.current, column.AttrName])];
              // }
              if (!!column.AttrTypeName && column.AttrTypeName.toLowerCase() === DATA_TYPES.DATE) {
                valueFormatter = (date) => rowDataDateFormatter(date, true);
              }
              if (!!column.AttrTypeName && column.AttrTypeName.toLowerCase() === DATA_TYPES.BOOLEAN) {
                valueFormatter = rowDataBoolFormatter;
              } else if (
                !!column.AttrTypeName &&
                (column.AttrTypeName.toLowerCase() === DATA_TYPES.PICKLIST ||
                  column.AttrTypeName.toLowerCase() === DATA_TYPES.LOOKUP)
              ) {
                valueFormatter = formatPicklistValues;
              } else if (
                !!column.AttrName &&
                column.AttrName.toLowerCase() === PAYLOAD_KEYS.READINGS_MANAGEMENT_PURCHASE_ID.toLowerCase()
              ) {
                valueFormatter = formatPicklistValues;
              } else if (
                !!column.AttrName &&
                column.AttrName.toLowerCase() === PAYLOAD_KEYS.READINGS_MANAGEMENT_TRANSPORTER_ID.toLowerCase()
              ) {
                valueFormatter = formatPicklistValues;
              } else if (
                !!column.AttrName &&
                (column.AttrName.toLowerCase() === 'ModifiedBy'.toLowerCase() ||
                  column.AttrName.toLowerCase() === 'CreatedBy'.toLowerCase())
              ) {
                valueFormatter = formatPicklistValues;
              } else if (column.Unit && column.Unit.toLowerCase() === 'inch') {
                valueFormatter = formatGaugeValues;
              } else if (column.Unit && column.Unit.toLowerCase() === 'seconds') {
                valueFormatter = formatTimeValues;
              } else if (
                column.AttrName === 'ModifiedTimestamp' ||
                column.AttrName === 'CreatedOn' ||
                column.AttrName === 'ModifiedOn'
              ) {
                valueFormatter = (params) => formatUTCtoLocalDateTime(params.value);
              }

              // Overriding formatters
              // disabled = isDisabled(column);

              if (column.AttrName === 'ReadingTime' || column.AttrName === 'TicketTime') {
                valueFormatter = convert24To12;
              } else if (
                column.AttrTypeName &&
                (column.AttrName.toLowerCase() === PAYLOAD_KEYS.READINGS_MANAGEMENT_TRANSPORTER_ID.toLowerCase() ||
                  column.AttrName.toLowerCase() === PAYLOAD_KEYS.READINGS_MANAGEMENT_PURCHASE_ID.toLowerCase())
              ) {
                // For Transporter and Purchaser, the type from backend comes as int but they are actually picklists
                // TODO : MAke and API call and get values before this, use those to get valueStr
              } else if (
                column.AttrTypeName &&
                column.AttrTypeName.toLowerCase() === DATA_TYPES.INTEGER &&
                !column.IsCalculated
              ) {
                valueFormatter = integerValueFormatter;
              } else if (
                column.AttrTypeName &&
                column.AttrTypeName.toLowerCase() === DATA_TYPES.STRING &&
                !column.IsCalculated
              ) {
                valueFormatter = (props) => props.value;
              } else if (
                column.AttrTypeName &&
                column.AttrTypeName.toLowerCase() === DATA_TYPES.DECIMAL &&
                column.Unit !== fractionUnitOfMeasure &&
                column.Unit !== secondsUnitOfMeasure
              ) {
                valueFormatter = (props) => props.value;
              }
              // set filters
            }
          }
          colObj.ValueStr = valueFormatter
            ? valueFormatter({ value: colObj?.Value, valueStr: colObj?.ValueStr })
            : colObj?.ValueStr;
          row[column.AttrName] = colObj?.Value ?? null;
          row.valueStr[column.AttrName] = colObj?.ValueStr ?? null;
        }
        if (pivotRecords[0].LineItemColumns.length !== 0) {
          records.push(row);
        }
      }
    }
    return {
      valueStr: records[0]?.valueStr,
      order: pivotColumns
        .filter((e) => addToGrid(e))
        .sort((a, b) => a.Sequence - b.Sequence)
        .map(({ AttrName, AttrDisplayName }) => {
          return { name: AttrName, displayName: AttrDisplayName };
        }),
    };
  }
  return {};
}
